import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Download, BookOpen, Zap, GraduationCap, Search, Printer,
} from "lucide-react";
import { HelpBlocks, blocksToPlain } from "@/components/HelpRenderer";

// ---------- Block renderers ----------
// (HelpBlocks renderer extraído a @/components/HelpRenderer)

const EXTRA_LABELS = {
  _whats_new: "✨ Novedades",
  _overview: "Introducción",
  _glossary: "Glosario DGII",
  _faq: "Preguntas Frecuentes",
  _workflows: "Flujos paso a paso",
  // Manual de configuración de impresora (apéndice)
  step1_install: "Paso 1 — Instalar el driver",
  step2_paper: "Paso 2 — Configurar papel continuo",
  step3_browser: "Paso 3 — Configurar el navegador",
  step4_default: "Paso 4 — Impresora predeterminada",
  step5_test: "Paso 5 — Prueba final",
  troubleshooting: "Solución de problemas",
  tips: "Recomendaciones",
};

// Convierte clave snake_case a Título Bonito si no hay mapeo en EXTRA_LABELS
function prettifyKey(k) {
  if (EXTRA_LABELS[k]) return EXTRA_LABELS[k];
  return k.replace(/^_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------- Manual viewer ----------
function ManualView({ manualId }) {
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setData(null);
    setActiveId(null);
    api.get(`/help/${manualId}`).then(({ data }) => {
      setData(data);
      const firstId =
        (data.sections._whats_new ? "_whats_new" : null) ||
        data.modules.find((m) => data.sections[m.id])?.id ||
        Object.keys(data.sections)[0];
      setActiveId(firstId);
    }).catch((e) => toast.error("No se pudo cargar el manual"));
  }, [manualId]);

  const sectionList = useMemo(() => {
    if (!data) return [];
    const moduleIds = new Set(data.modules.map((m) => m.id));
    const list = [];
    // _whats_new SIEMPRE primero si existe
    if (data.sections._whats_new) {
      list.push({ id: "_whats_new", label: EXTRA_LABELS._whats_new });
    }
    // Módulos del sistema
    data.modules.forEach((m) => {
      if (data.sections[m.id]) list.push({ id: m.id, label: m.title });
    });
    const EXTRAS = ["_glossary", "_faq", "_workflows"];
    // Cualquier clave del manual que NO sea módulo, _whats_new ni extra → al medio
    for (const k of Object.keys(data.sections)) {
      if (!moduleIds.has(k) && k !== "_whats_new" && !EXTRAS.includes(k)) {
        list.push({ id: k, label: prettifyKey(k) });
      }
    }
    for (const extra of EXTRAS) {
      if (data.sections[extra]) list.push({ id: extra, label: EXTRA_LABELS[extra] });
    }
    return list;
  }, [data]);

  const filteredList = useMemo(() => {
    if (!data || !query.trim()) return sectionList;
    const q = query.trim().toLowerCase();
    return sectionList.filter(({ id, label }) => {
      if (label.toLowerCase().includes(q)) return true;
      const plain = blocksToPlain(data.sections[id] || []);
      return plain.includes(q);
    });
  }, [data, sectionList, query]);

  // Si el query filtra, salta automáticamente a la primera coincidencia
  useEffect(() => {
    if (query && filteredList.length > 0 && !filteredList.some((s) => s.id === activeId)) {
      setActiveId(filteredList[0].id);
    }
  }, [query, filteredList, activeId]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await fetch(`${API}/help/${manualId}/pdf`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `manual_${manualId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error al descargar PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) {
    return <div className="p-8 text-center text-muted-foreground text-sm">Cargando manual…</div>;
  }

  const activeBlocks = data.sections[activeId] || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
      {/* Sidebar TOC */}
      <aside className="bg-card border border-border rounded-md p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
        <div className="px-2 pb-2">
          <div className="overline mb-1">{data.title}</div>
          <div className="text-xs text-muted-foreground mb-3">{data.subtitle}</div>
          <Button
            onClick={downloadPdf}
            disabled={downloading}
            size="sm"
            className="w-full bg-primary font-bold mb-3"
            data-testid={`download-pdf-${manualId}`}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {downloading ? "Generando…" : "Descargar PDF"}
          </Button>
          <div className="relative mb-2">
            <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-7 h-8 text-xs"
              data-testid="help-search"
            />
          </div>
        </div>
        <nav className="space-y-0.5">
          {filteredList.length === 0 && (
            <div className="text-xs text-muted-foreground px-2 py-3">Sin coincidencias</div>
          )}
          {filteredList.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              data-testid={`toc-${s.id}`}
              className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                activeId === s.id
                  ? "bg-primary/10 text-primary font-bold border-l-2 border-primary"
                  : "hover:bg-accent"
              }`}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <article className="bg-card border border-border rounded-md p-6 lg:p-8 min-h-[60vh]" data-testid={`manual-content-${manualId}`}>
        {activeBlocks.length === 0 ? (
          <div className="text-muted-foreground text-sm">Selecciona una sección del menú.</div>
        ) : (
          <HelpBlocks blocks={activeBlocks} />
        )}
      </article>
    </div>
  );
}

// ---------- Manual icons ----------
const MANUAL_ICONS = {
  quickstart: Zap,
  executive: BookOpen,
  technical: GraduationCap,
  printer_setup: Printer,
};

// ---------- Page root ----------
export default function Help() {
  const [manifest, setManifest] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/help/manifest").then(({ data }) => {
      setManifest(data);
      if (data.manuals?.[0]) setActive(data.manuals[0].id);
    }).catch(() => toast.error("No se pudo cargar el manifiesto de ayuda"));
  }, []);

  if (!manifest) {
    return (
      <PageContainer>
        <PageHeader title="Ayuda" subtitle="Manuales de uso del sistema." testid="help-header" />
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Centro de Ayuda"
        subtitle="Manuales de uso, glosario DGII, preguntas frecuentes y flujos paso a paso."
        testid="help-header"
      />

      <Tabs value={active || manifest.manuals[0]?.id} onValueChange={setActive} className="flex flex-col gap-5">
        <TabsList
          className="sticky top-0 z-20 h-auto bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border flex flex-wrap gap-2 p-3 -mx-4 px-4 sm:mx-0 sm:px-3 sm:rounded-md sm:border justify-start"
          data-testid="help-tabs-list"
        >
          {manifest.manuals.map((m) => {
            const Icon = MANUAL_ICONS[m.id] || BookOpen;
            return (
              <TabsTrigger
                key={m.id}
                value={m.id}
                data-testid={`manual-tab-${m.id}`}
                className="
                  group relative flex items-start gap-3 px-3.5 py-2.5
                  min-w-[200px] max-w-[260px] flex-1 sm:flex-none
                  rounded-md border border-border bg-card text-left transition-all
                  hover:border-primary/40 hover:bg-accent/40
                  data-[state=active]:border-primary data-[state=active]:bg-primary/10
                  data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-primary/20
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
                "
              >
                <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground group-data-[state=active]:text-primary" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold leading-tight text-foreground group-data-[state=active]:text-primary truncate">
                    {m.title}
                  </span>
                  <span className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
                    {m.estimated_pages}
                  </span>
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {manifest.manuals.map((m) => (
          <TabsContent key={m.id} value={m.id}>
            <ManualView manualId={m.id} />
          </TabsContent>
        ))}
      </Tabs>
    </PageContainer>
  );
}
